import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

// COMPONENTS
import Box from "@/main/components/shared/box";
import Button from "@/main/components/shared/button/button";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import useFormQueryParams from "@/main/hooks/useformQueryParams";
import { AreaButton } from "@/main/components/shared/styles";

// SERVICES / REDUX / UTILS
import {
	getDefaultCountryOptionDrop,
	getDefaultOptionDrop,
	getTranslateByKey,
	isNullUndefined,
	isValueQueryParams,
	removeEmptyValues,
} from "@/main/services/utils";
import { getIntegrationQueueStatus, useIntegrationQueueStatusReducer } from "@/main/store/integrationQueueStatus";

import {
	useIntegrationTypeReducer,
	getService,
	resetReducerIntegrationInterface,
	resetReducerService,
	getIntegrationInterface,
} from "@/main/store/integrationType";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { getBodyshops, resetReducerBodyshops, useBodyshopReducer } from "@/main/store/bodyshop";
import { getInsurers, resetReducerInsurers, useInsurerReducer } from "@/main/store/insurer";
import { KeyValue } from "@/main/models/common";
import { BodyShopFilterRequest, InsurerFilterRequest, IntegrationQueueRequest } from "@/main/models/request";
import { StatusRegisterEnum } from "@/main/services/enum/StatusRegisterEnum";
import { userIsBodyshop, userIsInsurer } from "@/main/services/utilsUser";
import { HeadQuarterBranchFilterEnum } from "@/main/services/enum/HeadQuarterBranchFilterEnum";

// SCREENS/COMPONENTS/CONTEXT LOCAL
import * as S from "./styles";
import { useParameterByTypeScreenReducer } from "@/main/store/parameterByTypeScreen";
import { ParameterTypeEnum } from "@/main/services/enum/ParameterTypeEnum";

interface IFiltersIntegrationProps {
	showFilters: boolean;
	setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
	onResetSearch: () => void;
	getDataIntegrationQueue: (filters: IntegrationQueueRequest) => void;
}

// #region Constants
const INITIAL_FORM_STATE = {
	country: null as KeyValue | null,
	integrationType: null as KeyValue | null,
	service: null as KeyValue | null,
	insurer: null as KeyValue | null,
	bodyshop: null as KeyValue | null,
	claimNumber: "" as string,
	status: null as KeyValue | null,
};

export type IFormFiltersIntegration = typeof INITIAL_FORM_STATE;
// #endregion

function FiltersIntegration({
	showFilters,
	setShowFilters,
	onResetSearch,
	getDataIntegrationQueue,
}: IFiltersIntegrationProps) {
	// #region Reducers
	const { integrationQueueStatus: integrationQueueStatusReducer } = useIntegrationQueueStatusReducer();
	const { service: serviceReducer, integrationInterface: intergrationInterfaceReducer } = useIntegrationTypeReducer();
	const { countriesUser: countryReducer } = useCountryReducer();
	const { bodyshops: bodyshopReducer } = useBodyshopReducer();
	const { insurers: insurerReducer } = useInsurerReducer();
	const PARAMETER_LENTGH_FILTER_CLAIM = useParameterByTypeScreenReducer(ParameterTypeEnum.ClaimScreenFilterClaimLength);

	// #endregion

	// #region Hooks
	const { control, queryParamsValues, reset, setValue, resetField, customHandleSubmit, watch } =
		useFormQueryParams<IFormFiltersIntegration>({
			defaultValues: INITIAL_FORM_STATE,
			onSubmit: onSubmitFilter,
			mapDefaultValues: {
				bodyshop: bodyshopReducer,
				insurer: insurerReducer,
				country: countryReducer,
				integrationType: serviceReducer,
				service: intergrationInterfaceReducer,
				status: integrationQueueStatusReducer,
			},
		});

	const dispatch = useDispatch();
	// #endregion

	const optionDefaultStatus = getDefaultCountryOptionDrop(integrationQueueStatusReducer.data);

	// #region States/Watch
	const watcherIntegrationType = watch("integrationType");
	const watcherCountry = watch("country");
	const optionDefaultInsurerDrop = userIsInsurer() ? getDefaultOptionDrop(insurerReducer.data) : null;
	const optionDefaultBodyshopDrop = getDefaultOptionDrop(bodyshopReducer.data);
	const [isIgnoreValidations, setIsIgnoreValidations] = useState<boolean>(false);

	// #endregion

	// #region Async Functions
	async function onSubmitFilter(data: IFormFiltersIntegration) {
		const mountRequest: IntegrationQueueRequest = removeEmptyValues({
			guidBodyshop: String(data.bodyshop?.key),
			guidInsurer: String(data.insurer?.key),
			guidCountry: String(data.country?.key),
			guidIntegrationQueueStatus: String(data.status?.key),
			guidIntegrationType: String(data.integrationType?.key),
			guidService: String(data.service?.key),
			claimNumber: data.claimNumber,
		});

		getDataIntegrationQueue(mountRequest);
		setShowFilters(false);
	}

	async function callIntegrationInterface(integrationTypeParam: KeyValue | null) {
		if (!isValueQueryParams(integrationTypeParam)) {
			setValue("service", null);
			dispatch(resetReducerIntegrationInterface());
		}

		if (integrationTypeParam === null) return;
		dispatch(
			getIntegrationInterface({
				idIntegrationType: 0,
				guidIntegration: String(integrationTypeParam.key),
			}),
		);
	}

	// #endregion

	// #region Handle Functions
	function handleClear() {
		const optionDefaultCountry = getDefaultOptionDrop(countryReducer.data);
		reset(INITIAL_FORM_STATE);
		onResetSearch();
		dispatch(resetReducerIntegrationInterface());
		dispatch(resetReducerService());
		// Get services withot filter integration type
		dispatch(getService(0));
		setValue("country", optionDefaultCountry);
		setValue("status", optionDefaultStatus);
		setIsIgnoreValidations(false);
	}

	function handleClearIgnore() {
		const optionDefaultCountry = getDefaultOptionDrop(countryReducer.data);

		setValue("bodyshop", INITIAL_FORM_STATE.bodyshop);
		setValue("insurer", INITIAL_FORM_STATE.insurer);
		setValue("integrationType", INITIAL_FORM_STATE.integrationType);
		setValue("service", INITIAL_FORM_STATE.service);
		setValue("country", optionDefaultCountry);
		setValue("status", optionDefaultStatus);

		dispatch(resetReducerIntegrationInterface());
		dispatch(resetReducerService());
		// Get services withot filter integration type
		dispatch(getService(0));
	}

	function handleIgnoreValidations(inputClaimNR: string) {
		if (inputClaimNR.length >= PARAMETER_LENTGH_FILTER_CLAIM) {
			setIsIgnoreValidations(true);
			return true;
		}
		if (inputClaimNR.length < PARAMETER_LENTGH_FILTER_CLAIM) {
			setIsIgnoreValidations(false);
			return false;
		}
		return null;
	}
	// #endregion

	// #region Async Functions
	function callBodyshopApiDomain(insurerSelected: KeyValue | null = null, isReset = false) {
		if (isNullUndefined(watcherCountry)) return;
		if (isReset) {
			dispatch(resetReducerBodyshops());
			resetField("bodyshop");
		}

		const request: BodyShopFilterRequest = {
			...(insurerSelected && { guidInsurer: String(insurerSelected.key) }),
			guidCountry: String(watcherCountry?.key),
			type: HeadQuarterBranchFilterEnum.All,
			idStatusRegister: StatusRegisterEnum.Active,
		};

		dispatch(getBodyshops(request));
	}
	function callInsurerApiDomain(bodyshopSelected: KeyValue | null = null, isReset = false) {
		if (isNullUndefined(watcherCountry)) return;
		if (isReset) {
			dispatch(resetReducerInsurers());
			resetField("insurer");
		}
		const request: InsurerFilterRequest = {
			...(bodyshopSelected && { guidBodyShop: String(bodyshopSelected.key) }),
			guidCountry: String(watcherCountry.key),
			type: HeadQuarterBranchFilterEnum.All,
			idStatusRegister: StatusRegisterEnum.Active,
		};

		dispatch(getInsurers(request));
	}

	// #endregion

	// #region UseEffects
	useEffect(() => {
		dispatch(getCountriesUser());
		// Get services withot filter integration type
		dispatch(getService(0));
		dispatch(getIntegrationQueueStatus());

		return () => {
			dispatch(resetReducerIntegrationInterface());
			dispatch(resetReducerService());
			dispatch(resetReducerInsurers());
			dispatch(resetReducerBodyshops());
		};
	}, []);

	useEffect(() => {
		const guidIntegrationTypeQueryParams = queryParamsValues.integrationType?.key;

		if (!isNullUndefined(guidIntegrationTypeQueryParams))
			callIntegrationInterface({
				key: guidIntegrationTypeQueryParams,
				value: "",
			});
	}, [JSON.stringify(queryParamsValues)]);

	useEffect(() => {
		if (watcherCountry === null) return;

		callBodyshopApiDomain();
		callInsurerApiDomain();
	}, [JSON.stringify(watcherCountry?.key)]);

	useEffect(() => {
		if (isIgnoreValidations) {
			setValue("country", null);
			setValue("integrationType", null);
			setValue("status", null);
			setValue("bodyshop", null);
			setValue("insurer", null);
			setValue("service", null);
		} else {
			handleClearIgnore();
		}
	}, [isIgnoreValidations]);
	// #endregion

	const disabledCountry = countryReducer?.data?.length === 1;
	const disabledIntegrationService =
		isNullUndefined(watcherIntegrationType) || intergrationInterfaceReducer.data?.length === 1;
	const hasBodyshopDefault = userIsBodyshop() && bodyshopReducer?.data?.length === 1;
	const disabledBodyshop = bodyshopReducer.data === null || hasBodyshopDefault;
	const disabledInsurer = userIsInsurer();

	return (
		<Box showBox={showFilters} title={getTranslateByKey("information.filters")}>
			<form onSubmit={customHandleSubmit} noValidate>
				<S.Container>
					<KogAutoCompleteControlled
						id="id-integrationQueue-select-country"
						nameCustom="country"
						label={getTranslateByKey("information.country")}
						isDefaultValueUnique
						control={control}
						rulesCustom={{ required: !isIgnoreValidations }}
						setValueCustom={setValue}
						options={countryReducer.data}
						isLoading={countryReducer.isLoading}
						disabled={disabledCountry}
						onChangeCustom={() => {}}
					/>
					<KogAutoCompleteControlled
						id="id-integrationQueue-select-integrationType"
						nameCustom="integrationType"
						label={getTranslateByKey("information.service")}
						isDefaultValueUnique
						control={control}
						rulesCustom={{ required: !isIgnoreValidations }}
						setValueCustom={setValue}
						options={serviceReducer.data}
						isLoading={serviceReducer.isLoading}
						disabled={false}
						onChangeCustom={callIntegrationInterface}
					/>
					<KogAutoCompleteControlled
						id="id-integrationQueue-select-service"
						nameCustom="service"
						label={getTranslateByKey("information.interface")}
						isDefaultValueUnique
						control={control}
						setValueCustom={setValue}
						options={intergrationInterfaceReducer.data}
						isLoading={intergrationInterfaceReducer.isLoading}
						disabled={disabledIntegrationService}
						onChangeCustom={() => {}}
					/>

					<KogAutoCompleteControlled
						id="id-integrationQueue-select-insurer"
						nameCustom="insurer"
						label={getTranslateByKey("information.insurer")}
						isDefaultValueUnique={optionDefaultInsurerDrop !== null}
						control={control}
						setValueCustom={setValue}
						options={insurerReducer.data}
						isLoading={insurerReducer.isLoading}
						disabled={disabledInsurer}
						onChangeCustom={(optionSelected: KeyValue | null) => {
							if (optionSelected === null) {
								if (optionDefaultBodyshopDrop != null) {
									callInsurerApiDomain(optionDefaultBodyshopDrop);
								} else {
									callInsurerApiDomain();
								}
							}
							callBodyshopApiDomain(optionSelected);
						}}
					/>
					<KogAutoCompleteControlled
						id="id-integrationQueue-select-bodyshop"
						nameCustom="bodyshop"
						label={getTranslateByKey("information.bodyshop")}
						isDefaultValueUnique
						control={control}
						setValueCustom={setValue}
						options={bodyshopReducer.data}
						isLoading={bodyshopReducer.isLoading}
						disabled={disabledBodyshop}
						onChangeCustom={(optionSelected: KeyValue | null) => {
							const isEmptyBodyshop = isNullUndefined(optionSelected);
							if (isEmptyBodyshop) callBodyshopApiDomain();
							if (!userIsInsurer()) callInsurerApiDomain(optionSelected);
						}}
					/>
					<KogInputTextControlledNew
						id="id-integrationQueue-txt-claimNumber"
						name="claimNumber"
						label={getTranslateByKey("information.claimNr")}
						control={control}
						autoComplete="off"
						onChangeCustom={(value) => {
							handleIgnoreValidations(value);
						}}
					/>
					<KogAutoCompleteControlled
						id="id-integrationQueue-select-status"
						nameCustom="status"
						label={getTranslateByKey("information.status")}
						valueDefaultKey={optionDefaultStatus?.key}
						control={control}
						rulesCustom={{ required: !isIgnoreValidations }}
						setValueCustom={setValue}
						options={integrationQueueStatusReducer.data}
						isLoading={integrationQueueStatusReducer.isLoading}
						disabled={false}
						onChangeCustom={() => {}}
					/>
					<S.AreaButtons>
						<SecondaryButton type="submit" id="id-integrationQueue-btn-submit">
							{getTranslateByKey("information.search")}
						</SecondaryButton>
						<SecondaryButton onClick={handleClear} id="id-integrationQueue-btn-clear">
							{getTranslateByKey("information.clear")}
						</SecondaryButton>
					</S.AreaButtons>
				</S.Container>
			</form>
		</Box>
	);
}
function FiltersIntegrationWrapper(props: Omit<IFiltersIntegrationProps, "showFilters" | "setShowFilters">) {
	//#region States
	const [showFilters, setShowFilters] = useState<boolean>(true);
	//#endregion

	//#region HandleFunctions
	function handleFilters() {
		setShowFilters((prev) => !prev);
		window.scrollTo({
			top: 0,
			behavior: "smooth",
		});
	}
	//#endregion

	const mergedProps: IFiltersIntegrationProps = {
		...props,
		showFilters,
		setShowFilters,
	};
	return (
		<>
			<AreaButton>
				<Button onClick={handleFilters} id="id-integrationQueue-btn-filters">
					{getTranslateByKey("information.filters")}
				</Button>
			</AreaButton>
			<FiltersIntegration {...mergedProps} />
		</>
	);
}
export default FiltersIntegrationWrapper;
